import { InlineIcon } from "@iconify/react";
import { Form, Formik } from "formik";
import _ from "lodash";
import { DateTime } from "luxon";
import React, { useContext, useState } from "react";
import { createUseStyles } from "react-jss";
import { GlobalModalsContext, NoScrollContext } from "../../context";
import jssVariables from "../../jss_setup";
import { modalGenericStyles } from "../../jss_setup/modals";
import { StyleTypeContext } from "../../umbilical_tools";
import { SearchButton } from "../common/Button";
import { DatepickerField } from "../forms/inputGroup/helpers";
import { PaxPicker } from "../MainSearchForm/widgets";
import RailPaxPickerModal from "../MainSearchForm/modals/RailPaxPickerModal";
import Toggle from "../forms/inputGroup/helpers/Toggle";
import {
  useTrainSearchFormHandle,
  validateForm,
} from "../MainSearchForm/train/helpers";
import { TrainStationsAutoCompleteField } from "../forms/inputGroup/helpers/TrainStationsAutoComplete";

const styles = createUseStyles({
  ...modalGenericStyles,
  DeepTrainSearchForm: (props) => ({
    ...modalGenericStyles.modal,
    position: "fixed",
    height: "100vh",
    width: "100vw",
    zIndex: 9999,
    ..._.get(props, "customStyles.DeepTrainSearchForm", {}),
  }),
  searchModal: (props) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    background: jssVariables.colors.background.light,
    padding: jssVariables.normal_gap,
    borderRadius: jssVariables.border_radius,
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.searchModal", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    justifyContent: "space-between",
    fontSize: "large",
    fontWeight: "bold",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.header", {}),
  }),

  closeIcon: (props) => ({
    fontSize: "xx-large",
    cursor: "pointer",
    ..._.get(props, "customStyles.closeIcon", {}),
  }),
  searchBtnContr: (props) => ({
    width: "max-content",
    margin: "auto",
    ..._.get(props, "customStyles.searchBtnContr", {}),
  }),
  formContainer: (props) => ({
    display: "grid",
    gridTemplateRows: "repeat(3, max-content)",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    padding: jssVariables.normal_gap,
    alignItems: "center",
    ..._.get(props, "customStyles.formContainer", {}),
  }),
  row1: (props) => ({
    ..._.get(props, "customStyles.row1", {}),
  }),
  row2: (props) => ({
    ..._.get(props, "customStyles.row2", {}),
  }),
  row3: (props) => ({
    ..._.get(props, "customStyles.row3", {}),
  }),
  mobileToggle: (props) => ({
    ..._.get(props, "customStyles.mobileToggle", {}),
  }),
  desktopToggle: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.desktopToggle", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    searchModal: (props) => ({
      width: "70rem",
      padding: jssVariables.double_gap,
      ..._.get(props, "customStyles.searchModal.smallscreen", {}),
    }),
    formContainer: (props) => ({
      ..._.get(props, "customStyles.formContainer.smallscreen", {}),
    }),
    row1: (props) => ({
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.row1.smallscreen", {}),
    }),
    row2: (props) => ({
      display: "grid",
      gridTemplateColumns: `repeat(${props.hasReturn ? 3 : 2}, 1fr)`,
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.row2.smallscreen", {}),
    }),
    row3: (props) => ({
      display: "grid",
      gridTemplateColumns: "repeat(2, max-content)",
      justifyContent: "space-between",
      alignItems: "end",
      ..._.get(props, "customStyles.row3.smallscreen", {}),
    }),
    mobileToggle: (props) => ({
      display: "none",
      ..._.get(props, "customStyles.mobileToggle.smallscreen", {}),
    }),
    desktopToggle: (props) => ({
      display: "block",
      ..._.get(props, "customStyles.desktopToggle.smallscreen", {}),
    }),
  },
});
const DeepTrainSearchForm = () => {
  const [showPaxPickerModal, setShowPaxPickerModal] = useState(false);
  const [hasReturn, setHasReturn] = useState(false);
  const { toggleNoScroll } = useContext(NoScrollContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { setShowTraFormModal } = useContext(GlobalModalsContext);

  const { search_form, onInitSearch, onClickPax, t } = useTrainSearchFormHandle(
    {
      setShowRailPaxPickerModal: setShowPaxPickerModal,
      setLoadingSearchBtn: () => {},
      isNewSearch: true,
    }
  );
  const customStyles = cStyler({
    type,
    brand,
    objName: "DeepTrainSearchForm",
  });

  const classes = styles({
    customStyles,
  });

  async function onNewSearch(form) {
    // const encoded_data = window.btoa(JSON.stringify(createPayload({ form })));
    const encoded_data = window.btoa(JSON.stringify({ ...form }));
    window.open(`/deep-train-search/${encoded_data}`, "_blank");
    setShowTraFormModal(false);
  }

  return (
    <div className={classes.DeepTrainSearchForm}>
      <div className={classes.searchModal}>
        <div className={classes.header}>
          <span>{t("performNewSearch")}</span>
          <InlineIcon
            icon="ei:close"
            className={classes.closeIcon}
            onClick={() => setShowTraFormModal(false)}
          />
        </div>
        <Formik
          initialValues={{
            origin: {
              origin_meta: _.get(search_form, "origin_meta", {}),
            },
            destination: {
              destination_meta: _.get(search_form, "destination_meta", {}),
            },
            departDateTime: [search_form.departDateTime],
            returnDateTime: [search_form.returnDateTime],
            hasReturn: false,
          }}
          validate={async (values) => {
            if (values.hasReturn !== hasReturn) {
              setHasReturn(values.hasReturn);
            }
          }}
          onSubmit={async (values) => {
            const hasErrors = await validateForm({
              ...search_form,
              ...values,
            });
            if (hasErrors) {
              return;
            }
            const form = {
              ...values,
              origin: values.origin.origin_meta[0],
              destination: values.destination.destination_meta[0],
              adults: search_form.adults,
              seniors: search_form.seniors,
              youths: search_form.youths,
              adults_ages: search_form.adults_ages,
              seniors_ages: search_form.seniors_ages,
              youths_ages: search_form.youths_ages,
            };

            onNewSearch(form);
          }}>
          <Form className={classes.formContainer}>
            <span className={classes.mobileToggle}>
              <Toggle name="hasReturn" label={t("return")} />
            </span>
            <div className={classes.row1}>
              <TrainStationsAutoCompleteField
                name="origin"
                label="origin"
                placeholder={t("origin")}
                withErrors={false}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
              <TrainStationsAutoCompleteField
                name="destination"
                label="destination"
                placeholder={t("destination")}
                withErrors={false}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
            </div>
            <div className={classes.row2}>
              <DatepickerField
                label={t("departure")}
                name="departDateTime"
                selectsRange={false}
                dateFormat="MMM d, yyyy hh:mm aa"
                minDate={DateTime.now().toISODate()}
                maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
                placeholder={"Mon DD, YYYY - Mon DD, YYYY"}
                withPortal={true}
                showTimeSelect={true}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
              {hasReturn && (
                <DatepickerField
                  label={t("return")}
                  name="returnDateTime"
                  selectsRange={false}
                  dateFormat="MMM d, yyyy hh:mm aa"
                  minDate={DateTime.now().toISODate()}
                  maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
                  placeholder={"Mon DD, YYYY - Mon DD, YYYY"}
                  withPortal={true}
                  showTimeSelect={true}
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                />
              )}
              <PaxPicker
                customStyles={_.get(customStyles, "inputGroupField", {})}
                adults={search_form.adults}
                adults_ages={search_form.adults_ages}
                childs={search_form.youths}
                children_ages={search_form.youths_ages}
                seniors={search_form.seniors}
                seniors_ages={search_form.seniors_ages}
                onClick={onClickPax}
                product_type="TRA"
              />
            </div>
            <div className={classes.row3}>
              <span className={classes.desktopToggle}>
                <Toggle name="hasReturn" label={t("return")} />
              </span>
              <div className={classes.searchBtnContr}>
                <SearchButton
                  id="traSearchBtn"
                  type="submit"
                  text={t("search")}
                  loading={false}
                  customStyles={_.get(customStyles, "searchBtn", {})}
                />
              </div>{" "}
            </div>
          </Form>
        </Formik>
      </div>
      {showPaxPickerModal && (
        <RailPaxPickerModal
          onCancel={() => {
            toggleNoScroll();
            setShowPaxPickerModal(false);
          }}
        />
      )}
    </div>
  );
};
DeepTrainSearchForm.propTypes = {};
export default DeepTrainSearchForm;
